import { CouponEntity } from "@modules/common/couponObject/entities/couponObjectEntity"
import { CouponDtoSchema } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { PriceObjectDtoSchema } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import { PaymentErrorDtoSchema } from "@modules/finances/types/stripeErrorTypes"
import { CoachDtoSchema } from "@modules/profile/coachProfile/coachDto"
import { UserDtoSchema } from "@modules/profile/userProfile/userDto"
import { ProgramBundleLightDtoSchema } from "@modules/programBundle/api/programBundleDto"
import {
  type ProgramPayment,
  ProgramStatus,
  ProgramSubscriptionStatus,
  ProgramType,
  ProgramVisibility,
} from "@modules/programs/types/programTypes"
import { LightPromoCodeEntity } from "@modules/promoCode/entities/lightPromoCodeEntity"
import { LightPromoCodeDtoSchema } from "@modules/promoCode/gateway/promoCodeDto"
import { SharedCoachDtoSchema } from "@modules/sharedCoach/gateway/sharedCoachDtos"
import { z } from "zod"

export const UserProgramDetailsDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    image: HUImageDtoSchema.nullable(),
    owner: CoachDtoSchema.nullable(),
    visibility: z.lazy(() => z.enum(ProgramVisibility)),
    subscriptionStatus: z.lazy(() => z.enum(ProgramSubscriptionStatus)),
    subscriptionCount: z.number(),
    price: PriceObjectDtoSchema.nullable(),
    entranceCheckEnabled: z.boolean(),
    invitationLink: z.string().nullable(),
    defaultHideSessionContent: z.boolean(),
    status: z.lazy(() => z.enum(ProgramStatus)),
    duration: z.number().nullable(),
    type: z.lazy(() => z.enum(ProgramType)),
    sessionCount: z.number(),
    trialDuration: z.number().nullable(),
    scoreboardVisibility: z.boolean(),
    programBundleSubscriptionId: z.string().nullable(),
    sharedCoaches: z.array(SharedCoachDtoSchema).nullable(),
    subscribed: z.boolean(),
    isEligibleToTrial: z.boolean().nullable(),
    programBundle: z.lazy(() => ProgramBundleLightDtoSchema.nullable()),
  })
  .meta({ id: "UserProgramDetailsDtoSchema" })

export const UpdateProgramPriceInputDtoSchema = z
  .object({
    price: PriceObjectDtoSchema,
    trialDuration: z.number().nullish(),
  })
  .meta({ id: "UpdateProgramPriceInputDtoSchema" })

export const ProgramSubscriptionDtoSchema = z
  .object({
    status: z.lazy(() => z.enum(ProgramSubscriptionStatus)),
  })
  .meta({ id: "ProgramSubscriptionDtoSchema" })

export type UserProgramDetailsDto = z.infer<typeof UserProgramDetailsDtoSchema>
export type UpdateProgramPriceInputDto = z.infer<typeof UpdateProgramPriceInputDtoSchema>
export type ProgramSubscriptionDto = z.infer<typeof ProgramSubscriptionDtoSchema>

// used in promocode API
export const LightProgramInfoDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    image: HUImageDtoSchema.nullable(),
    price: PriceObjectDtoSchema.nullable(),
    type: z.lazy(() => z.enum(ProgramType)),
  })
  .meta({ id: "LightProgramInfoDtoSchema" })

// used in b2b-programs API
export const WebLightProgramDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    image: HUImageDtoSchema.nullable(),
    owner: UserDtoSchema.nullable(),
    visibility: z.lazy(() => z.enum(ProgramVisibility)),
    scoreboardVisibility: z.boolean(),
    type: z.lazy(() => z.enum(ProgramType)),
  })
  .meta({ id: "WebLightProgramDtoSchema" })

export type LightProgramInfoDto = z.infer<typeof LightProgramInfoDtoSchema>
export type WebLightProgramDto = z.infer<typeof WebLightProgramDtoSchema>

export const ProgramPaymentDtoSchema = z
  .object({
    amount: PriceObjectDtoSchema.nullable(),
    promoCode: z.lazy(() => LightPromoCodeDtoSchema.nullable()), // Lazy to avoid circular dependency issues
    error: PaymentErrorDtoSchema.nullable(),
    coupon: CouponDtoSchema.nullable(),
  })
  .meta({ id: "ProgramPaymentDtoSchema" })

export type ProgramPaymentDto = z.infer<typeof ProgramPaymentDtoSchema>

export const ProgramOnOffPaymentDtoSchema = z
  .object({
    amount: PriceObjectDtoSchema.nullable(),
    promoCode: z.lazy(() => LightPromoCodeDtoSchema.nullable()), // Lazy to avoid circular dependency issues
    error: PaymentErrorDtoSchema.nullable(),
  })
  .meta({ id: "ProgramOnOffPaymentDtoSchema" })

export type ProgramOnOffPaymentDto = z.infer<typeof ProgramOnOffPaymentDtoSchema>

export function convertProgramPaymentFromDto(dto: ProgramPaymentDto | null): ProgramPayment | null {
  return dto?.amount
    ? {
        amount: PriceObjectEntity.fromDto(dto.amount),
        promoCode: dto.promoCode ? LightPromoCodeEntity.fromDto(dto.promoCode) : null,
        coupon: dto.coupon ? CouponEntity.fromDto(dto.coupon) : null,
        error: dto.error,
      }
    : null
}

export function convertOnOffProgramPaymentFromDto(dto: ProgramOnOffPaymentDto | null): ProgramPayment | null {
  return dto?.amount
    ? {
        amount: PriceObjectEntity.fromDto(dto.amount),
        promoCode: dto.promoCode ? LightPromoCodeEntity.fromDto(dto.promoCode) : null,
        coupon: null,
        error: dto.error,
      }
    : null
}
